import { History, UploadFile } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { DownloadFileNameLink, LabelField } from 'components'
import { useAuth } from 'context'
import * as controllers from 'controllers'
import { QuickBooksSyncBootstrapDto, QuickBooksSyncUploadDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { formatDateTime } from 'utils'
import QuickBooksSyncPreviousVersionsDialog from './QuickBooksSyncPreviousVersionsDialog'
import QuickBooksSyncUploadDialog from './QuickBooksSyncUploadDialog'

export default function QuickBooksSyncListCard() {
  const { permissions: ADMINISTRATION_ADD_EDIT } = useAuth()

  const [isGettingQuickBooksSync, setIsGettingQuickBooksSync] = useState<boolean>(false)
  const [isSavingQuickBooksSync, setIsSavingQuickBooksSync] = useState<boolean>(false)
  const [quickBooksSyncBootstrap, setQuickBooksSyncBootstrap] = useState<QuickBooksSyncBootstrapDto>(
    new QuickBooksSyncBootstrapDto()
  )
  const [quickBooksSyncUploadDialogOpen, setQuickBooksSyncUploadDialogOpen] = useState<boolean>(false)
  const [quickBooksSyncPreviousVersionsDialogOpen, setQuickBooksSyncPreviousVersionsDialogOpen] = useState<boolean>(false)
  const [quickBooksSyncUpload, setQuickBooksSyncUpload] = useState<QuickBooksSyncUploadDto>(new QuickBooksSyncUploadDto())

  const activeQuickBooksSync = quickBooksSyncBootstrap.activeQuickBooksSync

  useEffect(() => {
    getQuickBooksSync()
  }, [])

  const getQuickBooksSync = () => {
    setIsGettingQuickBooksSync(true)
    controllers
      .getQuickBooksSync()
      .then(response => {
        setQuickBooksSyncBootstrap(response.value)
      })
      .finally(() => {
        setIsGettingQuickBooksSync(false)
      })
  }

  return (
    <>
      <QuickBooksSyncUploadDialog
        initialValues={quickBooksSyncUpload}
        isLoading={isSavingQuickBooksSync}
        onClose={() => {
          setQuickBooksSyncUploadDialogOpen(false)
        }}
        onSave={values => {
          setIsSavingQuickBooksSync(true)
          controllers
            .uploadQuickBooksSync(values)
            .then(response => {
              enqueueSnackbar('QuickBooks Sync Uploaded Successfully!', { variant: 'success' })
              setQuickBooksSyncBootstrap(response.value)
              setQuickBooksSyncUploadDialogOpen(false)
            })
            .finally(() => {
              setIsSavingQuickBooksSync(false)
            })
        }}
        open={quickBooksSyncUploadDialogOpen}
      />

      <QuickBooksSyncPreviousVersionsDialog
        onClose={() => {
          setQuickBooksSyncPreviousVersionsDialogOpen(false)
        }}
        open={quickBooksSyncPreviousVersionsDialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  QuickBooks Sync
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  disabled={!quickBooksSyncBootstrap.hasPreviousVersions}
                  endIcon={<History />}
                  onClick={() => {
                    setQuickBooksSyncPreviousVersionsDialogOpen(true)
                  }}
                  variant='text'
                >
                  VERSION HISTORY
                </Button>

                {ADMINISTRATION_ADD_EDIT && (
                  <Button
                    endIcon={<UploadFile />}
                    onClick={() => {
                      setQuickBooksSyncUpload({
                        ...new QuickBooksSyncUploadDto(),
                        quickBooksVersion: activeQuickBooksSync?.quickBooksVersion ?? ''
                      })
                      setQuickBooksSyncUploadDialogOpen(true)
                    }}
                    variant='text'
                  >
                    UPLOAD
                  </Button>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingQuickBooksSync && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid item>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingQuickBooksSync && (
              <>
                <Grid item xs={12}>
                  <LabelField
                    label='Application'
                    value={
                      activeQuickBooksSync?.document?.displayFileName ? (
                        <DownloadFileNameLink
                          fileName={activeQuickBooksSync.document.displayFileName}
                          label='Download'
                        />
                      ) : (
                        ''
                      )
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='QuickBooks Version' value={activeQuickBooksSync?.quickBooksVersion} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Notes' value={activeQuickBooksSync?.notes} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Uploaded At'
                    value={
                      activeQuickBooksSync?.createdDateTime ? formatDateTime(activeQuickBooksSync.createdDateTime) : undefined
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Uploaded By'
                    value={`${activeQuickBooksSync?.createdBy?.firstName ?? ''} ${
                      activeQuickBooksSync?.createdBy?.lastName ?? ''
                    }`.trim()}
                  />
                </Grid>
              </>
            )}

          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
